import type { ErrorCode } from "@hik/contracts";

/** Sunucudan dönen hata; `message` kullanıcının dilinde ve çözüm yolunu söyler. */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: ErrorCode | "network",
    message: string,
    public fields: Record<string, string> = {},
    public details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

let locale = "tr";

const UNAUTH_REASON_KEY = "hik:unauth-reason";

/** Son 401'in nedeni (ör. tenant_suspended, support_ended): giriş ve "destek oturumu sona erdi" ekranları okur. */
function rememberUnauthReason(reason: string | null) {
  try {
    if (reason) sessionStorage.setItem(UNAUTH_REASON_KEY, reason);
    else sessionStorage.removeItem(UNAUTH_REASON_KEY);
  } catch {
    /* depolama kapalı */
  }
}

export function takeUnauthReason(): string | null {
  try {
    const r = sessionStorage.getItem(UNAUTH_REASON_KEY);
    sessionStorage.removeItem(UNAUTH_REASON_KEY);
    return r;
  } catch {
    return null;
  }
}

export function peekUnauthReason(): string | null {
  try {
    return sessionStorage.getItem(UNAUTH_REASON_KEY);
  } catch {
    return null;
  }
}
export function setApiLocale(l: string) {
  locale = l;
}

type Opts = { idempotencyKey?: string; signal?: AbortSignal };

/**
 * Tüm istekler aynı kaynaktan (Vite proxy / aynı sunucu) çerezle yapılır.
 * Değiştirici isteklerde `X-HIK-Client: web` başlığı CSRF korumasının parçasıdır.
 */
export async function api<T>(method: string, path: string, body?: unknown, opts: Opts = {}): Promise<T> {
  const headers: Record<string, string> = { "x-hik-client": "web", "accept-language": locale };
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers["content-type"] = "application/json";
    payload = JSON.stringify(body);
  }
  if (opts.idempotencyKey) headers["idempotency-key"] = opts.idempotencyKey;
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, { method, headers, body: payload, credentials: "same-origin", signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "network", locale === "en" ? "Couldn't connect. Check your connection and try again; your input is kept." : "Bağlantı kurulamadı. İnternetinizi kontrol edip tekrar deneyin; girdiğiniz bilgiler korunuyor.");
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = data?.error ?? {};
    const apiErr = new ApiError(res.status, err.code ?? "internal_error", err.message ?? res.statusText, err.fields ?? {}, err.details ?? {});
    if (res.status === 401) {
      // Platform paneli ayrı oturumdur: panelin 401'i firma oturum önbelleğine dokunmaz.
      if (path.startsWith("/platform/")) window.dispatchEvent(new CustomEvent("hik:platform-unauthenticated", { detail: apiErr.code }));
      else {
        rememberUnauthReason(typeof apiErr.details.reason === "string" ? apiErr.details.reason : null);
        window.dispatchEvent(new CustomEvent("hik:unauthenticated", { detail: apiErr.code }));
      }
    }
    throw apiErr;
  }
  return data as T;
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>("GET", path, undefined, { signal });
export const post = <T>(path: string, body?: unknown, opts?: Opts) => api<T>("POST", path, body ?? {}, opts);
/** Okuma amaçlı POST (önizleme gibi), iptal sinyaliyle. */
export const postQuery = <T>(path: string, body: unknown, signal?: AbortSignal) => api<T>("POST", path, body, { signal });
export const put = <T>(path: string, body?: unknown, opts?: Opts) => api<T>("PUT", path, body ?? {}, opts);
export const patch = <T>(path: string, body?: unknown, opts?: Opts) => api<T>("PATCH", path, body ?? {}, opts);
export const del = <T>(path: string) => api<T>("DELETE", path);

export function qs(params: Record<string, unknown>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** Dosya yükleme (multipart). */
export async function uploadFile(file: File, purpose: string, ownerEmployeeId?: string) {
  const fd = new FormData();
  fd.set("purpose", purpose);
  if (ownerEmployeeId) fd.set("ownerEmployeeId", ownerEmployeeId);
  fd.set("file", file);
  return api<{ id: string; name: string; mimeType: string; sizeBytes: number }>("POST", "/files", fd);
}

export const fileUrl = (id: string, download = false) => `/api/v1/files/${id}${download ? "?download=true" : ""}`;

/** Tekrar gönderimlerde aynı kalan anahtar (çift tıklama / ağ yeniden denemesi güvenli). */
export function newIdempotencyKey() {
  return crypto.randomUUID();
}
